<script setup lang="ts">
import {reactive, ref, useTemplateRef, nextTick, watch} from "vue";
import {Button, Drawer, Textarea, ProgressSpinner} from "@/Components/Library";
import {useToast} from "primevue/usetoast";
import {getHistory, generate as generateViaApi, clearHistory} from "@/Services/write-with-ai";
import markdownit from "markdown-it";
import Str from "@/Utils/Str";

const toast = useToast();
const md = markdownit();

const model = defineModel<string | null>();
const {
    parent = "",
    parentId = "",
    field = "",
} = defineProps<{parent?: string; parentId?: string | null; field: string}>();

const modalVisible = ref(false);
const prompt = ref("");
const messages = ref<{role: "user" | "ai"; text?: string}[]>([]);
const promptField = useTemplateRef("promptField");

const state = reactive({
    loading: {
        generation: false,
        history: false,
    },
});

const loadHistory = async () => {
    if (!parent) {
        return;
    }

    try {
        state.loading.history = true;
        const {history} = await getHistory({parent, parent_id: parentId, field});
        messages.value = history || [];
    } catch (error) {
        console.error("Failed to load history:", error);
    } finally {
        state.loading.history = false;
    }
};

const clear = async () => {
    if (!confirm("Are you sure you want to clear the chat history?")) {
        return;
    }

    try {
        state.loading.history = true;
        await clearHistory({parent, parent_id: parentId, field});
        messages.value = [];
        prompt.value = "";
    } catch (error) {
        console.error("Failed to clear history:", error);
        toast.add({severity: "error", summary: "Error", detail: "Failed to clear history.", life: 3000});
    } finally {
        state.loading.history = false;
    }
};

const generate = async () => {
    try {
        const currentPrompt = prompt.value;
        const isFirstMessage = messages.value.length === 0;
        const text = isFirstMessage ? model.value : messages.value.filter((m) => m.role === "ai").pop()?.text;

        if (isFirstMessage && !currentPrompt && (!text || text.length < 10)) {
            toast.add({
                severity: "error",
                summary: "Error",
                detail: "Please add some instructions for the AI.",
                life: 3000,
            });
            return;
        }

        state.loading.generation = true;

        const data = await generateViaApi({
            parent,
            parent_id: parentId,
            text,
            field,
            prompt: currentPrompt,
        });

        messages.value.push({role: "user", text: data.prompt});
        prompt.value = "";
        messages.value.push({role: "ai", text: data.generated_text});

        await nextTick();
        promptField.value?.$el?.scrollIntoView({behavior: "smooth", block: "start"});
    } catch (error) {
        console.error(error);
        messages.value.pop();
        toast.add({severity: "error", summary: "Error", detail: "Failed to generate text with AI.", life: 3000});
    } finally {
        state.loading.generation = false;
    }
};

const accept = (text: string) => {
    model.value = text;
    modalVisible.value = false;
};

const reset = () => {
    messages.value = [];
    prompt.value = "";
};

watch(modalVisible, (visible) => {
    if (visible) {
        loadHistory();
    }
});
</script>

<template>
    <div>
        <Button
            v-tooltip.left="`Write with AI`"
            type="button"
            class="!p-2 text-askr-green"
            :icon="{icon: 'ri:robot-2-fill', width: 24}"
            @click="modalVisible = true"
        />
        <Drawer
            v-model:visible="modalVisible"
            position="right"
            header="Write with AI"
            class="w-full max-w-2xl"
            @hide="reset"
        >
            <div class="flex w-full flex-col gap-4 overflow-y-auto">
                <div v-if="model" class="border-b pb-6 dark:border-stone-700">
                    {{ model }}
                </div>
                <div v-if="state.loading.history" class="flex flex-col items-center gap-2 py-6">
                    <ProgressSpinner />
                    <p class="text-center text-lg font-semibold">Loading history...</p>
                </div>

                <div v-else-if="messages.length">
                    <div class="mb-4 flex justify-end">
                        <Button
                            v-tooltip.left="`Clear History`"
                            label="Clear History"
                            :icon="{icon: 'prime:trash'}"
                            severity="secondary"
                            size="small"
                            :loading="state.loading.history"
                            @click="clear"
                        />
                    </div>
                    <template v-for="(message, i) in messages" :key="i">
                        <div
                            class="mb-6 p-4"
                            :class="[
                                message.role === 'user'
                                    ? 'rounded-lg border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-800'
                                    : 'border-l-4 border-stone-300 dark:border-stone-600',
                            ]"
                        >
                            <p v-if="message.role === 'user'">{{ message.text }}</p>
                            <template v-else>
                                <div class="flex items-start justify-between gap-2">
                                    <div
                                        class="prose prose-stone flex-1 dark:prose-invert"
                                        v-html="Str.of(md.render(message.text ?? 'There was an error.')).toString()"
                                    ></div>
                                    <Button
                                        v-tooltip.left="'Accept this version'"
                                        type="button"
                                        :icon="{icon: 'prime:check'}"
                                        size="small"
                                        class="shrink-0"
                                        @click="accept(message.text!)"
                                    />
                                </div>
                            </template>
                        </div>
                    </template>
                </div>

                <form @submit.prevent="generate" class="relative">
                    <Textarea
                        ref="promptField"
                        id="prompt"
                        v-model="prompt"
                        placeholder="Enter instructions..."
                        class="w-full pr-14 text-base"
                        :required="messages.length === 0 && !model"
                        auto-resize
                    />
                    <Button
                        type="submit"
                        class="!absolute bottom-3 right-3 !p-0"
                        :loading="state.loading.generation"
                        :icon="{icon: 'prime:send'}"
                    />
                </form>

                <div v-if="state.loading.generation" class="flex flex-col items-center gap-2 py-6">
                    <ProgressSpinner />
                    <p class="text-center text-lg font-semibold">Generating...</p>
                </div>
            </div>
        </Drawer>
    </div>
</template>

<style scoped></style>
